/**
 * Sync Queue - IndexedDB
 * 
 * Queues offline mutations and retries with exponential backoff
 */

import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { logger } from './logger';

export interface QueuedMutation {
  id: string;
  type: 'order_update' | 'gps_update' | 'chat_message' | 'order_accept';
  payload: any;
  timestamp: string;
  retryCount: number;
  nextRetryAt: string;
  maxRetries: number;
}

interface SyncDB extends DBSchema {
  mutations: {
    key: string;
    value: QueuedMutation;
    indexes: { 'by-retry': string };
  };
}

class SyncQueue {
  private db: IDBPDatabase<SyncDB> | null = null;
  private processing = false;
  private retryIntervals = [1000, 2000, 4000, 8000, 16000, 30000]; // Exponential backoff

  async init(): Promise<void> {
    try {
      this.db = await openDB<SyncDB>('rider-sync-queue', 1, {
        upgrade(db) {
          const store = db.createObjectStore('mutations', { keyPath: 'id' });
          store.createIndex('by-retry', 'nextRetryAt');
        },
      });

      logger.info('Sync queue initialized');
    } catch (error) {
      logger.error('Failed to initialize sync queue', error as Error);
    }
  }

  /**
   * Add mutation to queue
   */
  async add(mutation: Omit<QueuedMutation, 'retryCount' | 'nextRetryAt' | 'maxRetries'>): Promise<void> {
    if (!this.db) await this.init();

    const queuedMutation: QueuedMutation = {
      ...mutation,
      retryCount: 0,
      nextRetryAt: new Date().toISOString(),
      maxRetries: 5,
    };

    await this.db!.put('mutations', queuedMutation);
    logger.info('Mutation queued', { id: mutation.id, type: mutation.type });

    // Start processing if not already
    if (!this.processing) {
      this.processQueue();
    }
  }

  /**
   * Get all pending mutations
   */
  async getAll(): Promise<QueuedMutation[]> {
    if (!this.db) await this.init();
    return await this.db!.getAll('mutations');
  }

  /**
   * Get mutations ready for retry
   */
  async getReadyForRetry(): Promise<QueuedMutation[]> {
    if (!this.db) await this.init();
    const all = await this.db!.getAll('mutations');
    const now = new Date().toISOString();
    
    return all.filter(m => m.nextRetryAt <= now && m.retryCount < m.maxRetries);
  }

  /**
   * Remove mutation from queue
   */
  async remove(id: string): Promise<void> {
    if (!this.db) await this.init();
    await this.db!.delete('mutations', id);
    logger.info('Mutation removed from queue', { id });
  }

  /**
   * Update retry count and schedule next retry
   */
  async incrementRetry(id: string): Promise<void> {
    if (!this.db) await this.init();
    const mutation = await this.db!.get('mutations', id);
    
    if (!mutation) return;

    mutation.retryCount++;
    
    // Calculate next retry time with exponential backoff
    const delayIndex = Math.min(mutation.retryCount - 1, this.retryIntervals.length - 1);
    const delay = this.retryIntervals[delayIndex];
    mutation.nextRetryAt = new Date(Date.now() + delay).toISOString();

    await this.db!.put('mutations', mutation);
    logger.info('Mutation retry scheduled', {
      id,
      retryCount: mutation.retryCount,
      nextRetryAt: mutation.nextRetryAt,
    });
  }

  /**
   * Process queue (called on connect or timer)
   */
  async processQueue(): Promise<void> {
    if (this.processing) return;
    if (!navigator.onLine) {
      logger.warn('Cannot process queue - offline');
      return;
    }

    this.processing = true;
    logger.info('Processing sync queue');

    try {
      const mutations = await this.getReadyForRetry();
      logger.info('Mutations ready for retry', { count: mutations.length });

      for (const mutation of mutations) {
        try {
          await this.processMutation(mutation);
          await this.remove(mutation.id);
          logger.info('Mutation processed successfully', {
            id: mutation.id,
            type: mutation.type,
          });
        } catch (error) {
          logger.error('Failed to process mutation', error as Error, {
            id: mutation.id,
            type: mutation.type,
          });

          if (mutation.retryCount >= mutation.maxRetries) {
            logger.warn('Mutation max retries reached, removing', { id: mutation.id });
            await this.remove(mutation.id);
          } else {
            await this.incrementRetry(mutation.id);
          }
        }
      }
    } finally {
      this.processing = false;
    }

    // Schedule next check
    const remaining = await this.getAll();
    if (remaining.length > 0) {
      setTimeout(() => this.processQueue(), 5000);
    }
  }

  /**
   * Process a single mutation
   */
  private async processMutation(mutation: QueuedMutation): Promise<void> {
    // Import API client lazily to avoid circular dependencies
    const { ordersApi, chatApi, riderApi } = await import('./api');

    switch (mutation.type) {
      case 'order_accept':
        await ordersApi.accept(mutation.payload.orderId);
        break;

      case 'order_update':
        await ordersApi.updateStatus(mutation.payload.orderId, mutation.payload.status);
        break;

      case 'chat_message':
        await chatApi.sendMessage(mutation.payload.threadId, mutation.payload.message);
        break;

      case 'gps_update':
        await riderApi.updateLocation(
          mutation.payload.lat,
          mutation.payload.lng,
          mutation.payload.accuracy
        );
        break;

      default:
        logger.warn('Unknown mutation type', { type: mutation.type });
    }
  }

  /**
   * Clear all mutations
   */
  async clearAll(): Promise<void> {
    if (!this.db) await this.init();
    await this.db!.clear('mutations');
    logger.info('Sync queue cleared');
  }

  /**
   * Get queue count
   */
  async getCount(): Promise<number> {
    if (!this.db) await this.init();
    return await this.db!.count('mutations');
  }
}

export const syncQueue = new SyncQueue();


















